import { Divider, Grid2 as Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FileSearch01, Trash01 } from 'untitledui-js-base';
import Lightbox from 'yet-another-react-lightbox';
import { BoxWithHeader, CustomDropzone } from '../common';
import { AccidentMediaItem, MediaValue, PhotoNoteType } from '../types/accident';

interface AccidentPhotographyBoxProps {
  title: string;
  description?: string;
  helpPhotoUrl?: string;
  defaultPhotos?: MediaValue[];
  isRequired?: boolean;
  onUpload?: (photo: File[]) => void;
  onRemove?: (fileId: number) => void;
  showThumbs?: boolean;
  isRemoving?: boolean;
  note?: string;
  onNoteSave?: (value: string) => void;
}

export const AccidentPhotographyBox = React.memo(
  ({
    title,
    description,
    helpPhotoUrl,
    defaultPhotos,
    isRequired,
    onUpload,
    onRemove,
    showThumbs = true,
    note,
    onNoteSave
  }: AccidentPhotographyBoxProps) => {
    const [images, setImages] = useState<File[] | null>(null);
    const [hasError, setHasError] = useState(false);

    const [openLightbox, setOpenLightbox] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const slides =
      defaultPhotos &&
      defaultPhotos?.map((value) => ({
        src: value.webP ? value.webP : value.url
      }));

    const handleImageClick = (index: number) => {
      setCurrentIndex(index);
      setOpenLightbox(true);
    };

    const handleSetImage = useCallback(
      (images: File[]) => {
        setImages(images);
        onUpload?.(images);
      },
      [onUpload]
    );

    const handleRemove = useCallback(
      (fileId: number) => {
        onRemove?.(fileId);
      },
      [onRemove]
    );

    const DefaultImages = React.memo(() => {
      if (!defaultPhotos || defaultPhotos.length === 0) return null;

      return (
        <>
          <Typography variant="h6" fontWeight="bold" mt={3} mb={1}>
            Nahrané fotografie
          </Typography>
          <Stack spacing={2} mt={3}>
            {defaultPhotos.map((image, index) => {
              const src = image instanceof File ? URL.createObjectURL(image) : image.webP ? image.webP : image.url;

              return (
                <Stack
                  key={image.id || index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.300',
                    paddingBottom: '10px'
                  }}
                >
                  <Stack alignItems="center" spacing={2} direction="row">
                    <Box sx={{ aspectRatio: 1, width: '50px' }}>
                      <Box
                        component="img"
                        src={src}
                        maxWidth={1}
                        loading="lazy"
                        onClick={() =>
                          handleImageClick(
                            defaultPhotos.findIndex((slide) => (slide.webP ? slide.webP : slide.url) === src)
                          )
                        }
                        sx={{
                          transition: 'opacity 0.3s',
                          opacity: 1,
                          height: 1,
                          width: 1,
                          objectFit: 'cover',
                          objectPosition: 'center',
                          cursor: 'pointer'
                        }}
                      />
                    </Box>
                    <Typography variant="body2">{image.fileName}</Typography>
                  </Stack>
                  <IconButton
                    color="error"
                    onClick={() => handleRemove(image.id)}
                    size="small"
                    sx={{ width: '30px', height: '30px', flexShrink: 0 }}
                  >
                    <Trash01 />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        </>
      );
    });

    useEffect(() => {
      if (isRequired && !images && (!defaultPhotos || defaultPhotos.length === 0)) {
        setHasError(true);
      } else {
        setHasError(false);
      }
    }, [images, isRequired, defaultPhotos]);

    return (
      <>
        <BoxWithHeader
          fullHeight
          title={
            <Stack direction="row" gap={1} alignItems="center">
              {helpPhotoUrl && (
                <>
                  <IconButton color="primary" size="small" onClick={() => setOpenLightbox(true)}>
                    <FileSearch01 size="20px" />
                  </IconButton>
                  <Divider orientation="vertical" />
                </>
              )}
              <Stack>
                <Typography variant="h6" fontWeight="bold">
                  {title}
                </Typography>
                {description && <Typography variant="caption">{description}</Typography>}
              </Stack>
            </Stack>
          }
          style={{
            border: '1px solid',
            borderColor: hasError ? 'red' : 'transparent',
            transition: 'all 0.3s ease'
          }}
          extra={
            hasError && (
              <Typography color="red" fontSize={12}>
                Fotografie je povinná
              </Typography>
            )
          }
        >
          <Grid container>
            <Grid size={12} display="flex" flexDirection="column" justifyContent="space-between" gap={2} height={1}>
              <CustomDropzone
                title="Nahrajte fotky nehody"
                onLoadedFiles={handleSetImage}
                showFiles={showThumbs}
                maxFiles={20}
              />
              <TextField
                defaultValue={note ?? ''}
                label="Poznámka k fotografiím"
                color="secondary"
                fullWidth
                InputLabelProps={{ shrink: true }}
                multiline
                rows={2}
                onChange={(event) => onNoteSave?.(event.target.value)}
              />
            </Grid>
          </Grid>
          <DefaultImages />
        </BoxWithHeader>

        {defaultPhotos && !isEmpty(defaultPhotos) && (
          <Lightbox open={openLightbox} close={() => setOpenLightbox(false)} index={currentIndex} slides={slides} />
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (!prevProps.defaultPhotos && !nextProps.defaultPhotos) {
      return true;
    }

    if (!prevProps.defaultPhotos || !nextProps.defaultPhotos) {
      return false;
    }

    if (Array.isArray(prevProps.defaultPhotos) && Array.isArray(nextProps.defaultPhotos)) {
      const prevIds = prevProps.defaultPhotos.map((p) => p?.id).filter(Boolean);
      const nextIds = nextProps.defaultPhotos.map((p) => p?.id).filter(Boolean);
      return JSON.stringify(prevIds) === JSON.stringify(nextIds);
    }

    return prevProps.defaultPhotos === nextProps.defaultPhotos;
  }
);

interface AccidentCreatingStepPhotoProps {
  images?: AccidentMediaItem[];
  onUpload?: (category: string, images: File[]) => void;
  onRemoveFile?: (template: string, id: number) => void;
  isUploading?: boolean;
  note?: string;
  onNoteSave?: (data: PhotoNoteType) => void;
}

export const AccidentCreatingStepPhoto = React.memo(
  ({ images, onUpload, onRemoveFile, isUploading, note, onNoteSave }: AccidentCreatingStepPhotoProps) => {
    const handleUpload = useCallback(
      (category: string, photos: File[]) => {
        onUpload?.(category, photos);
      },
      [onUpload]
    );

    const handleRemove = useCallback(
      (template: string, fileId: number) => {
        onRemoveFile?.(template, fileId);
      },
      [onRemoveFile]
    );

    return (
      <>
        <Grid container spacing={4} mt={2}>
          {images?.map((image, index) => (
            <Grid size={{ xs: 12, lg: 6 }} key={index}>
              <AccidentPhotographyBox
                title={image.label}
                onUpload={(photo) => handleUpload(image.name, photo)}
                defaultPhotos={image?.values as MediaValue[]}
                onRemove={(fileId) => handleRemove(image.name, fileId)}
                isRemoving={isUploading}
                showThumbs={false}
                note={note}
                onNoteSave={(value) =>
                  onNoteSave?.({
                    note: value,
                    photoName: image.name
                  })
                }
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  },
  (prevProps, nextProps) => {
    if (!prevProps.images && !nextProps.images) {
      return true;
    }

    if (!prevProps.images || !nextProps.images) {
      return false;
    }

    if (prevProps.images.length !== nextProps.images.length) {
      return false;
    }

    const prevImagesIds = prevProps.images.flatMap((img) => {
      if (!img.values || !Array.isArray(img.values)) {
        return [];
      }
      return (img.values as MediaValue[]).map((v) => v?.id).filter(Boolean);
    });

    const nextImagesIds = nextProps.images.flatMap((img) => {
      if (!img.values || !Array.isArray(img.values)) {
        return [];
      }
      return (img.values as MediaValue[]).map((v) => v?.id).filter(Boolean);
    });

    return JSON.stringify(prevImagesIds) === JSON.stringify(nextImagesIds);
  }
);
